import { useApolloClient, useMutation } from "@apollo/client";
import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  AdminCreateUserAttributeCategoryMutation,
  AdminCreateUserAttributeCategoryMutationVariables,
  AdminCreateUserAttributeMutation,
  AdminCreateUserAttributeMutationVariables,
  TagCategoryFragment,
  UserAttributeFragment,
  UserAttributeListQuery,
} from "gql/graphql";
import React, { memo, useCallback, useMemo, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { SERVER_ERROR } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import AdminCreateUserAttributeSelect, {
  INewUserAttributeOption,
} from "./components/admin-create-user-attribute-select";
import ADMIN_CREATE_USER_ATTRIBUTE_CATEGORY_MUTATION from "./_networking/admin-create-user-attribute-category";
import ADMIN_CREATE_USER_ATTRIBUTE_MUTATION from "./_networking/admin-create-user-attribute";
import USER_ATTRIBUTE_LIST_QUERY from "modules/providers/user-attributes-provider/networking/user-attribute-list";
import { useUserAttributes } from "modules/providers/user-attributes-provider";
import AdminTagCategorySelect from "./components/admin-tag-category-select";
import { getMessageFromError } from "modules/utils/Functions";

const AdminAddUserAttributeCategoryModal = () => {
  const { closeModal } = useOverlay();
  const { cache } = useApolloClient();
  const { userAttributeCategories } = useUserAttributes();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [value, setValue] = useState<readonly INewUserAttributeOption[]>([]);
  const [tagCategory, setTagCategory] = useState<TagCategoryFragment | null>();

  const excludeTagCategoryIds = useMemo(
    () =>
      userAttributeCategories
        .map((userAttributeCategory) => userAttributeCategory.tagCategoryId)
        .filter((id): id is string => Boolean(id)),
    [userAttributeCategories],
  );

  const [adminCreateUserAttributeCategoryMutationAPI] = useMutation<
    AdminCreateUserAttributeCategoryMutation,
    AdminCreateUserAttributeCategoryMutationVariables
  >(ADMIN_CREATE_USER_ATTRIBUTE_CATEGORY_MUTATION);

  const [adminCreateUserAttributeMutationAPI] = useMutation<
    AdminCreateUserAttributeMutation,
    AdminCreateUserAttributeMutationVariables
  >(ADMIN_CREATE_USER_ATTRIBUTE_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    watch,
  } = useForm({
    mode: "onSubmit",
  });

  const name = watch("name");

  const duplicateUserAttributes = useMemo(
    () =>
      value
        .filter((v) => !!v.duplicateUserAttribute)
        .map((v) => v.duplicateUserAttribute!),
    [value],
  );

  const duplicateUserAttributeCategory = useMemo(
    () =>
      userAttributeCategories.find(
        (category) => category.name.toLowerCase() === name?.toLowerCase(),
      ),
    [name, userAttributeCategories],
  );

  const hasDuplicateUserAttribute = duplicateUserAttributes.length > 0;

  const isSubmitDisabled = hasDuplicateUserAttribute;

  const onSubmit = useCallback(
    async ({ name }: FieldValues) => {
      if (duplicateUserAttributeCategory) {
        setError("name", {
          type: "validate",
          message: "This name already exists",
        });
        return;
      }
      setIsSubmitting(true);
      adminCreateUserAttributeCategoryMutationAPI({
        variables: {
          input: {
            name,
            ...(tagCategory?.id ? { tagCategoryId: tagCategory.id } : {}),
          },
        },
        onError: (error: Error) => {
          setIsSubmitting(false);
          setError(SERVER_ERROR, {
            message: getMessageFromError(error.message),
          });
        },
        onCompleted: async (data) => {
          setIsSubmitting(false);
          const userAttributeCategory = data?.adminCreateUserAttributeCategory;
          if (!userAttributeCategory) {
            return;
          }
          if (value.length === 0) {
            closeModal();
            return;
          }
          const results = await Promise.all(
            value.map((name) =>
              adminCreateUserAttributeMutationAPI({
                variables: {
                  input: {
                    name: name.value,
                    userAttributeCategoryId: userAttributeCategory.id,
                  },
                },
              }),
            ),
          );
          const createdUserAttributes: UserAttributeFragment[] = results
            .map((result) => result.data?.adminCreateUserAttribute)
            .filter(
              (userAttribute): userAttribute is UserAttributeFragment =>
                !!userAttribute,
            );
          setIsSubmitting(false);
          if (createdUserAttributes) {
            cache.updateQuery<UserAttributeListQuery>(
              {
                query: USER_ATTRIBUTE_LIST_QUERY,
              },
              (data) => {
                if (!data) {
                  return data;
                }
                return {
                  ...data,
                  userAttributeList: [
                    ...data.userAttributeList,
                    ...createdUserAttributes,
                  ],
                };
              },
            );
          }
          closeModal();
        },
      });
    },
    [
      adminCreateUserAttributeCategoryMutationAPI,
      adminCreateUserAttributeMutationAPI,
      cache,
      closeModal,
      duplicateUserAttributeCategory,
      setError,
      tagCategory?.id,
      value,
    ],
  );

  return (
    <Modal isOpen onClose={closeModal} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
              Add User Attributes
            </Text>
          </Center>
        </ModalHeader>
        <ModalBody>
          <form
            id="admin-add-user-attribute-category-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Stack alignItems="flex-start" spacing={6}>
              <FormControl isInvalid={!!errors?.name}>
                <VStack w="full" alignItems="flex-start" spacing={1}>
                  <Text
                    fontSize={16}
                    fontWeight={700}
                    color={colors.blackSecondary}
                  >
                    Attribute Category Name
                  </Text>
                  <Input
                    id="name"
                    placeholder="Create unique name"
                    _placeholder={{
                      color: colors.placeholderText,
                      fontStyle: "italic",
                    }}
                    {...register("name", {
                      required: {
                        message: "a name is required",
                        value: true,
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {getMessageFromError(errors?.name?.message)}
                  </FormErrorMessage>
                </VStack>
              </FormControl>
              <FormControl>
                <Text
                  fontSize={16}
                  fontWeight={700}
                  color={colors.blackSecondary}
                >
                  Use an existing tag category and tags (optional)
                </Text>
                <AdminTagCategorySelect
                  onChange={setTagCategory}
                  excludeTagCategoryIds={excludeTagCategoryIds}
                />
              </FormControl>
              {!tagCategory && (
                <FormControl>
                  <VStack w="full" spacing={2} alignItems="flex-start">
                    <Text
                      fontSize={16}
                      fontWeight={700}
                      color={colors.blackSecondary}
                    >
                      Attributes
                    </Text>
                    <Text fontSize={12} fontWeight={400} color={colors.gray30}>
                      Type words and add a comma or hit return to set attribute
                    </Text>
                    <AdminCreateUserAttributeSelect onChange={setValue} />
                    <Text fontSize={12} fontWeight={400} color={colors.gray30}>
                      Special characters allowed: dashes, underscores and
                      periods
                    </Text>
                  </VStack>
                </FormControl>
              )}
            </Stack>
            {hasDuplicateUserAttribute && (
              <Text color={colors.red}>Duplicate attributes found</Text>
            )}
            {errors.SERVER_ERROR && (
              <Text color={colors.red}>
                {getMessageFromError(errors?.SERVER_ERROR?.message)}
              </Text>
            )}
            <Center pt={5}>
              <HStack>
                <Button
                  variant="cancel"
                  height={8}
                  w={28}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  height={8}
                  w={28}
                  onClick={() => {
                    clearErrors([SERVER_ERROR]);
                  }}
                  type="submit"
                  isDisabled={isSubmitDisabled}
                >
                  {isSubmitting ? <Spinner /> : "Save"}
                </Button>
              </HStack>
            </Center>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(AdminAddUserAttributeCategoryModal);
